/**
 * A mesa de 4 em Linha da Sala de Descanso.
 *
 * O propósito é o que separa isto de decoração: na sala de descanso de um
 * escritório real não se trabalha — joga-se uma partida enquanto se fala. A mesa
 * dá isso mesmo: DOIS jogam, toda a gente na call VÊ o mesmo tabuleiro a mudar,
 * e a conversa continua por cima (a voz nunca é interrompida, o painel é um
 * overlay sobre o palco, não uma sala nova).
 *
 * Três decisões, as mesmas do quadro branco e pela mesma razão:
 *
 *  1. SEM BACKEND. Viaja tudo pelo canal de dados do LiveKit, o SFU que já leva
 *     a voz. Zero rotas novas na API, zero tabelas: a partida existe enquanto a
 *     call existir, que é exactamente o tempo em que interessa a alguém.
 *  2. JOGADAS, NÃO TABULEIROS. Envia-se a coluna; cada cliente aplica as regras
 *     puras do `@greenvoice/shared` e chega ao mesmo tabuleiro. O protocolo e a
 *     convergência vivem no `game4Net.ts` — testados à parte, longe do DOM.
 *  3. AQUI SÓ MORA O ECRÃ. Este ficheiro pinta e traduz cliques em mensagens.
 *     42 casas em React não são um problema de desempenho (ao contrário do
 *     canvas do quadro, que tem de fugir ao `setState`) — por isso o estado é
 *     `useState` honesto, e o `ref` existe só para o callback do fio, que é
 *     registado uma vez e não pode ficar preso a um estado velho.
 */

import { C4_COLS, C4_ROWS, type C4Player, c4CanDrop, c4Index } from "@greenvoice/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { voiceEngine } from "../lib/voice";
import { useStore } from "../store/store";
import {
  type G4Msg,
  type G4Slot,
  g4Empty,
  g4IsMyTurn,
  g4MoveMsg,
  g4Parse,
  g4Prune,
  g4Ready,
  g4Reduce,
  g4SeatOf,
  type TableState,
} from "./game4Net";
import { IconClose, IconUndo } from "./Icons";

/** As duas cores, na ordem dos lugares: 1 abre a vermelho, 2 responde a amarelo. */
const CORES: Record<C4Player, string> = { 1: "#e0483c", 2: "#f0c020" };

/** As colunas e as linhas como VALORES, não como índices de um `map`. A
 *  diferença importa ao React: a coluna 3 é a coluna 3 em todos os renders, e é
 *  isso que a torna uma chave honesta — a lista nunca reordena. */
const COLUNAS: readonly number[] = Array.from({ length: C4_COLS }, (_, i) => i);
const LINHAS: readonly number[] = Array.from({ length: C4_ROWS }, (_, i) => i);

/** O que está numa coluna, dito por palavras. É o que um leitor de ecrã lê no
 *  botão — sem isto, "Coluna 3" não diz se lá jogar ganha ou perde a partida. */
function descreveColuna(state: TableState, col: number): string {
  let vermelhas = 0;
  let amarelas = 0;
  for (const row of LINHAS) {
    const disc = state.game.cells[c4Index(col, row)] ?? 0;
    if (disc === 1) vermelhas += 1;
    else if (disc === 2) amarelas += 1;
  }
  if (!vermelhas && !amarelas) return "vazia";
  const partes: string[] = [];
  if (vermelhas) partes.push(vermelhas === 1 ? "1 vermelha" : `${vermelhas} vermelhas`);
  if (amarelas) partes.push(amarelas === 1 ? "1 amarela" : `${amarelas} amarelas`);
  return partes.join(", ") + (vermelhas + amarelas >= C4_ROWS ? ", cheia" : "");
}

export function GameTable({
  open,
  active,
  onClose,
}: {
  /** Visível? Fechado fica montado (a seguir a partida) mas `display:none` — é o
   *  que permite fechar o painel, andar pelo escritório e voltar a encontrar o
   *  jogo onde estava, sem pedir sync outra vez. */
  open: boolean;
  /** Ligado à call? Sem identidade local não há como sentar-se nem jogar. */
  active: boolean;
  onClose: () => void;
}) {
  const [state, setState] = useState<TableState>(g4Empty);

  // O callback do canal de dados é registado UMA vez (re-subscrever a cada
  // jogada perdia pacotes na troca). Como tal não pode ler `state` — leria o do
  // render em que foi criado. Este espelho é a leitura sempre actual.
  const stateRef = useRef(state);
  stateRef.current = state;

  const me = active ? voiceEngine.localId : null;
  const meRef = useRef(me);
  meRef.current = me;

  const users = useStore((s) => s.users);
  const selfId = useStore((s) => s.self?.id ?? null);

  /** O nome de quem se senta. A identidade do LiveKit é o `userId`, por isso o
   *  nome vem do store — o `ParticipantView` não o traz. */
  const nomeDe = useCallback(
    (id: string | null): string => {
      if (!id) return "";
      if (id === selfId) return "Tu";
      const u = users[id];
      return u ? (u.displayName ?? u.username) : "Alguém";
    },
    [users, selfId],
  );

  /** Aplica localmente e envia. O reducer é determinístico, por isso aplicar já
   *  aqui não é adivinhar o futuro: é chegar ao mesmo sítio uns milissegundos
   *  antes dos outros. (O LiveKit não devolve ao remetente o que ele enviou.) */
  const enviar = useCallback((m: G4Msg) => {
    setState((s) => g4Reduce(s, m));
    voiceEngine.sendData(m, true);
  }, []);

  // ── canal de dados ──────────────────────────────────────────

  useEffect(() => {
    const off = voiceEngine.subscribeData((_from, data) => {
      const m = g4Parse(data);
      if (!m) return;
      if (m.k === "g4:hello") {
        // Alguém chegou. Só responde quem tem mesmo alguma coisa para contar;
        // vários a responderem é inofensivo, porque o `sync` mais curto perde.
        const s = stateRef.current;
        if (s.moves.length || g4Ready(s) || s.seats[0] || s.seats[1]) {
          voiceEngine.sendData({ k: "g4:sync", to: m.by, seats: [s.seats[0], s.seats[1]], moves: [...s.moves] }, true);
        }
        return;
      }
      if (m.k === "g4:sync" && m.to !== voiceEngine.localId) return;
      setState((s) => g4Reduce(s, m));
    });
    return off;
  }, []);

  // Ao ligar à call, pede a partida a quem já lá está.
  useEffect(() => {
    if (active) voiceEngine.sendData({ k: "g4:hello", by: voiceEngine.localId ?? "" }, true);
  }, [active]);

  // Quem fechou o portátil larga o lugar. Ninguém carrega em "levantar-me" antes
  // de sair, e um lugar preso a um fantasma é uma mesa que nunca mais serve.
  // Não precisa de pacote: a lista de presentes é igual em todos os ecrãs.
  useEffect(() => {
    const limpar = () => {
      const presentes = new Set(voiceEngine.getSnapshot().participants.map((p) => p.identity));
      if (presentes.size) setState((s) => g4Prune(s, presentes));
    };
    limpar();
    return voiceEngine.subscribe(limpar);
  }, []);

  // ── o que se pode fazer agora ───────────────────────────────

  const meuLugar = g4SeatOf(state, me);
  const minhaVez = g4IsMyTurn(state, me);
  const pronta = g4Ready(state);
  const congelada = state.moves.length > 0;

  const sentar = (slot: G4Slot) => {
    if (!me || congelada) return;
    enviar({ k: "g4:sit", by: me, slot });
  };

  const jogar = (col: number) => {
    if (!me || !minhaVez || !c4CanDrop(state.game, col)) return;
    enviar(g4MoveMsg(state, me, col));
  };

  const recomecar = () => {
    if (!me || meuLugar === null || !state.moves.length) return;
    enviar({ k: "g4:new", by: me });
  };

  /** As casas da linha vencedora, para as acender. */
  const ganhou = useMemo(() => new Set(state.game.line), [state.game.line]);

  const estado = ((): string => {
    if (!active) return "Entra na chamada para jogar.";
    const { outcome, turn } = state.game;
    if (outcome === 3) return "Empate — 42 peças e ninguém cedeu.";
    if (outcome === 1 || outcome === 2) {
      const quem = state.seats[outcome - 1] ?? null;
      return quem === me ? "Ganhaste!" : `${nomeDe(quem)} ganhou.`;
    }
    if (!pronta) {
      if (meuLugar === null) return "Senta-te num lugar para jogar.";
      return "À espera de alguém do outro lado.";
    }
    if (minhaVez) return "É a tua vez.";
    if (meuLugar !== null) return `Vez de ${nomeDe(state.seats[turn - 1] ?? null)}.`;
    return `A jogar: ${nomeDe(state.seats[0])} e ${nomeDe(state.seats[1])}.`;
  })();

  return (
    <div className={`g4${open ? "" : " g4--hidden"}`}>
      <div className="g4__panel" role="dialog" aria-label="Mesa de 4 em Linha">
        <header className="g4__head">
          <h2 className="g4__title">4 em Linha</h2>
          <button type="button" className="g4__icon" onClick={onClose} title="Fechar a mesa">
            <IconClose size={16} />
          </button>
        </header>

        <div className="g4__seats">
          {([1, 2] as const).map((slot) => {
            const quem = state.seats[slot - 1];
            const euAqui = meuLugar === slot;
            const vez = pronta && state.game.outcome === 0 && state.game.turn === slot;
            return (
              <button
                key={slot}
                type="button"
                className={`g4__seat${euAqui ? " is-me" : ""}${vez ? " is-turn" : ""}`}
                // Sentar é livre até cair a primeira peça; depois disso os
                // lugares congelam e o botão passa a ser só uma etiqueta.
                disabled={!active || congelada || (quem !== null && !euAqui)}
                onClick={() => (euAqui ? enviar({ k: "g4:stand", by: me ?? "" }) : sentar(slot))}
                title={euAqui ? "Levantar-me" : quem ? nomeDe(quem) : "Sentar-me aqui"}
              >
                <span className="g4__chip" style={{ background: CORES[slot] }} aria-hidden="true" />
                <span className="g4__who">{quem ? nomeDe(quem) : "Lugar livre"}</span>
                {euAqui && !congelada ? <span className="g4__hint">sair</span> : null}
              </button>
            );
          })}
        </div>

        {/* Um `fieldset`, e não uma grelha: quem usa leitor de ecrã não precisa
            de navegar 42 casas — precisa de saber em que coluna joga e o que lá
            está. Sete controlos relacionados com nome próprio é exactamente o
            que um `fieldset` descreve, e a interface acessível são eles. */}
        <fieldset className="g4__board" aria-label="Tabuleiro">
          {COLUNAS.map((col) => (
            <button
              key={col}
              type="button"
              className="g4__col"
              // A coluna inteira é o alvo: acertar numa casa exacta com o rato
              // seria precisão a troco de nada — a peça cai onde a gravidade
              // mandar, não onde se clicou.
              disabled={!minhaVez || !c4CanDrop(state.game, col)}
              onClick={() => jogar(col)}
              aria-label={`Coluna ${col + 1}: ${descreveColuna(state, col)}`}
            >
              {LINHAS.map((row) => {
                const disc = state.game.cells[c4Index(col, row)] ?? 0;
                const acesa = ganhou.has(c4Index(col, row));
                return (
                  <span
                    key={row}
                    className={`g4__cell${disc ? " is-filled" : ""}${acesa ? " is-win" : ""}`}
                    style={disc ? { background: CORES[disc] } : undefined}
                  />
                );
              })}
            </button>
          ))}
        </fieldset>

        <footer className="g4__foot">
          <span className="g4__status">{estado}</span>
          <button
            type="button"
            className="g4__icon"
            onClick={recomecar}
            disabled={meuLugar === null || !state.moves.length}
            title="Nova partida"
          >
            <IconUndo size={16} />
          </button>
        </footer>
      </div>
    </div>
  );
}
